import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { ContactDetailsContainer } from '@components/ContactDetails/CommonStyles/ContactDetailsContainer';

import { addModelLog } from '@libs/NwLogs';

import { useStateValue } from '@contexts/StateProvider';
import { useUserPermission } from '@contexts/UserContext';

import { useContact } from '@hooks/API/Contact/UseContact';

import ContactDetailsTasks from '@components/ContactDetails/CommonComponents/ContactDetailsTasks';
import ModelDetailsBooksWrapper from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsBooks/ModelDetailsBooksWrapper';
import ModelDetailsComments from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsComments';
import ModelDetailsCustomPropertiesAndNotesTabs from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsCustomPropertiesAndNotesTabs';
import ModelDetailsMedia from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsMedia/ModelDetailsMedia';
import MeetingEventsCard from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsMeetings/MeetingEventsCard';
import ModelRatings from '@components/ContactDetails/ModelDetails/CommonComponents/ModelRatings/ModelRatings';

import ContactDetailsActions from '@components/ContactDetails/ContactDetailsHeader/ContactDetailsActions';
import ModelDetailsHeaderDrawer from '@components/ContactDetails/ContactDetailsHeader/ModelDetailsHeaderDrawer';
import ContactDetailsDocuments from '@components/ContactDetailsDocuments/ContactDetailsDocuments';
import ObjectHistory from '@components/CreatedBy/ObjectHistory';
import FeaturesDrawer from '@components/Features/FeaturesDrawer';
import FlexContainer from '@components/Gui/FlexContainer';
import { Loading } from '@components/Gui/Loading';
import NwDrawer from '@components/Gui/NwDrawer';
import NwIcon from '@components/Gui/NwIcon';
import ModelAgenciesCard from '@components/ModelAgencies/ModelAgenciesCard';
import ObjectLogHistories from '@components/ObjectLogHistories/ObjectLogHistories';
import PermissionBlock from '@components/PermissionBlock/PermissionBlock';
import ContactSocials from '@components/Social/ContactSocials';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Actions = styled(FlexContainer)`
    gap: 2rem;
    margin-right: 1rem;
`;

// Model preview for the scouting area: same shell as ModelDrawerBooking,
// with the scouting cards of ModelDetailsPage (ratings, meetings, comments) instead of the agenda.
const ModelDrawerScouting = ({ modelID, scoutingModel, meetingImages, onClose, zIndex }) => {
    const { data: currentContact, isLoading } = useContact(modelID);
    const [{ areaContext }] = useStateValue();
    const navigate = useNavigate();
    const [isOpenHistory, setIsOpenHistory] = useState(false);
    const [editFeatures, setEditFeatures] = useState(false);

    const viewModelAgencies = useUserPermission(
        'ViewModelAgencies',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const viewModelFeatures = useUserPermission(
        'ViewModelFeatures',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );

    useEffect(() => {
        currentContact &&
            currentContact.NetworkAgencies &&
            !currentContact.NetworkAgencies[0].StorageBooking &&
            addModelLog(currentContact, areaContext.area);
    }, [currentContact]);

    const handleMoveModelDetails = () => {
        navigate(`/model/${modelID}`);
    };

    const handleCloseFeatureDrawer = () => {
        setEditFeatures(false);
    };

    const handleOpenFeatureDrawer = () => {
        setEditFeatures(true);
    };

    const handleOpenHistory = () => {
        setIsOpenHistory(true);
    };

    const handleCloseHistory = () => {
        setIsOpenHistory(false);
    };

    const renderAction = (content, permission) => {
        return <PermissionBlock content={content} permission={permission} />;
    };

    return (
        <NwDrawer
            width={780}
            title={
                currentContact ? (
                    <FlexContainer justifyContent="space-between">
                        <div>Model Preview</div>
                        <Actions>
                            <NwIcon
                                icon={byPrefixAndName.fass['expand-wide']}
                                pointer
                                onClick={handleMoveModelDetails}
                                size="lg"
                            />
                            {renderAction(
                                <NwIcon
                                    icon={byPrefixAndName.fasl['weight']}
                                    size="lg"
                                    onClick={viewModelFeatures && handleOpenFeatureDrawer}
                                />,
                                viewModelFeatures
                            )}
                            <ObjectHistory iconOnly onIconClick={handleOpenHistory} object={currentContact} />
                            <ContactDetailsActions contact={currentContact} fromDrawer onClose={onClose} />
                        </Actions>
                    </FlexContainer>
                ) : (
                    'Model Preview'
                )
            }
            onClose={onClose}
            whiteBg={false}
            zIndex={zIndex}
        >
            <div className="drawer-content">
                {isLoading || !currentContact ? (
                    <Loading />
                ) : (
                    <ContactDetailsContainer>
                        <ModelDetailsHeaderDrawer
                            selectedModel={currentContact.ID}
                            onOpenModelAgencies={() => {}}
                            onOpenStatements={() => {}}
                        />
                        <div className="content-section">
                            <ModelRatings modelId={currentContact.ID} />
                        </div>
                        <div className="content-section">
                            <ContactDetailsTasks currentContact={currentContact} />
                        </div>
                        <div className="content-section">
                            <ModelDetailsCustomPropertiesAndNotesTabs currentContact={currentContact} drawerMode />
                        </div>
                        <div className="content-section">
                            <MeetingEventsCard contactID={currentContact.ID} />
                        </div>
                        <div className="content-section">
                            <ModelDetailsComments currentContact={currentContact} drawerMode />
                        </div>
                        <div className="content-section">
                            <ContactSocials previewMode currentContact={currentContact} />
                        </div>
                        <div className="content-section">
                            <ModelDetailsBooksWrapper
                                contactID={modelID}
                                scope="model-drawer"
                                modelPlaylists={currentContact.Playlists}
                            />
                        </div>
                        <div className="content-section">
                            {/* From a scouting meeting, media opens on the meeting pictures */}
                            <ModelDetailsMedia
                                noDots
                                currentContact={currentContact}
                                scoutingModel={scoutingModel}
                                previewMode
                                meetingMediaList={meetingImages}
                            />
                        </div>
                        {viewModelAgencies && (
                            <div className="content-section">
                                <ModelAgenciesCard
                                    contactID={currentContact.ID}
                                    represented={currentContact.Represented}
                                    drawerMode
                                />
                            </div>
                        )}
                        <div className="content-section">
                            <ContactDetailsDocuments currentContact={currentContact} readOnly />
                        </div>
                    </ContactDetailsContainer>
                )}
                {isOpenHistory && (
                    <NwDrawer title="Model Logs" onClose={handleCloseHistory}>
                        <ObjectLogHistories objectId={currentContact.ID} type="model" />
                    </NwDrawer>
                )}
                {editFeatures && (
                    <FeaturesDrawer contact={currentContact} readOnly={true} onClose={handleCloseFeatureDrawer} />
                )}
            </div>
        </NwDrawer>
    );
};

export default ModelDrawerScouting;
